import sys from '@truckdown/jsclient';
import * as apis from "@truckdown/jsclient/apis";
import { ILocationsUtilities } from "./ILocationsUtilities";
import * as locations from '@truckdown/locations';
import { IScriptLoader } from './IScriptLoader';

interface geoAddress {
    label: string,
    countryName: string,
    countryCode: string,
    state: string,
    stateCode: string,
    city: string,
    street: string
}

interface geoAddressResult {
    address: geoAddress,
    position: geoCoordinate
}

interface geoCoordinate {
    lat: number,
    lng: number
}

interface geoResult {
    items: geoAddressResult[]
}

export class LocationsUtilities implements ILocationsUtilities {
    constructor() {
        this.loadServices = this._loadServices(this);
        this.loadRegions = this._loadRegions(this);
        this.scriptLoader = sys.framework.getRequiredService<IScriptLoader>('IScriptLoader');
    }

    private async _loadServices (obj: ILocationsUtilities) {
        let sharedEntities = sys.framework.getRequiredService<apis.tools.ISharedEntitiesManager>('ISharedEntitiesManager');
        let all = await sharedEntities.allServices({
            status: apis.tools.ServiceStatus.Active,
            sites: [sys.framework.context.sys]
        });

        obj.services = all
            .sort(function (a, b) { return a.priority - b.priority });
    }

    private async _loadRegions (obj: ILocationsUtilities) {
        let sharedEntities = sys.framework.getRequiredService<apis.tools.ISharedEntitiesManager>('ISharedEntitiesManager');
        obj.regions = await sharedEntities.allRegions();
    }

    scriptLoader: IScriptLoader;

    loadServices: Promise<void>;
    loadRegions: Promise<void>;

    services: apis.tools.Service[] = [];
    regions: apis.tools.Region[] = [];

    private getServiceUrlPortion (request: locations.SearchRequestModel) {
        if (request.services.length > 1) {
            return '/multiple-services';
        } else if (request.services.length == 1) {
            let service = this.services.find((svs) => {
                return svs.code == request.services[0];
            });
            if (service) {
                return '/' + encodeURIComponent(service.name);
            }
        }
        return '/unknown';
    }

    private getLocationUrlPortion(request: locations.SearchRequestModel) {
        let location = request.meta['locationDisplay'] as string ?? request.meta['city'] as string;
        if (location) {
            return '/' + encodeURIComponent(location);
        }
        return '/unknown';
    }

    private getCityPortion(request: locations.SearchRequestModel) {
        let location = request.meta['cityId'] as string;
        if (location) {
            return '/' + encodeURIComponent(location);
        }
        return '';
    }

    private getServicesQuery(request: locations.SearchRequestModel) {
        return '?svs=' + request.services.join('-');
    }

    private getAmentityQuery(request: locations.SearchRequestModel) {
        if (request.filters.length > 0) {
            return '&ams=' + request.filters.join('-');
        }
        return '';
    }

    private getOptionsQuery(request: locations.SearchRequestModel) {
        return (request.options.searchByDistance ? '&sod=true' : '')
            + (request.options.suppressManagedServices ? '&sms=true' : '');
    }

    private getLocationQuery(request: locations.SearchRequestModel) {
        let location = request.meta['cityId'] as string;
        //no options if has a cityId
        if (location) {
            return '';
        }

        return '&lat=' + request.meta['lat']
            + '&lng=' + request.meta['lng']
            + '&ctryCd=' + request.meta['countryCode']
            + '&rgnCd=' + request.meta['regionCode'];
    }

    generateUrl (request: locations.SearchRequestModel) {
        return '/search/results'
            + this.getServiceUrlPortion(request)
            + this.getLocationUrlPortion(request)
            + this.getCityPortion(request)
            + this.getServicesQuery(request)
            + this.getLocationQuery(request)
            + this.getAmentityQuery(request)
            + this.getOptionsQuery(request);
    }


    private defaultCity(lat: number, lng: number) {
        var city = new apis.tools.City();
        city.id = '';
        city.name = 'Current Location';
        city.regionCode = '';
        city.countryCode = '';
        city.point = {
            lat: lat,
            lng: lng
        };
        return city;
    }

    private getPlatfrom() {
        var platform = sys.getSetting<H.service.Platform>('here_platform');
        if (platform == null) {
            throw 'Here platform not configured';
        }
        return platform;
    }

    private getCountryCode(code: string) {
        if (code == 'CAN') {
            return 'CA';
        }
        if (code == 'MEX') {
            return 'MX';
        }
        return 'US';
    }

    async asyncReverseGeocode (lat: number, lng: number) {
        let platform = await this.scriptLoader.loadHereMaps();
        let geocoder = (platform as any).getSearchService() as any;

        let obj = this;

        let geocode = new Promise<apis.tools.City>((resolve, reject) => {
            let handleError = (error: unknown) => {
                console.error(error);
                resolve(obj.defaultCity(lat, lng));
            }

            try {
                let handleResult = (result: geoResult) => {
                    if (result.items && result.items.length > 0) {
                        var city = new apis.tools.City();
                        city.id = '';
                        city.name = result.items[0].address.label ?? result.items[0].address.city ?? 'Current Location';
                        city.regionCode = result.items[0].address.stateCode;
                        city.countryCode = obj.getCountryCode(result.items[0].address.countryCode);
                        city.point = {
                            lat: lat,
                            lng: lng
                        };
                        resolve(city);
                    } else {
                        resolve(obj.defaultCity(lat, lng));
                    }
                };

                geocoder.reverseGeocode({
                    at: lat + ',' + lng
                }, handleResult, handleError);
            }
            catch (err) {
                handleError(err);
            }
        });

        return await geocode;
    }
}


export default (framework: apis.framework.IFramework) => {
    framework.registerService<ILocationsUtilities>('ILocationsUtilities', new LocationsUtilities());
}